/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGlobal } from 'reactn';
import { useNavigate } from 'react-router-dom';
import { TagEnum, FeedTypeEnum } from '@orijinworks/frontend-commons';
import {
  getDMFeeds,
  getFeedMessages,
  createMessage,
  getFeedById,
  markFeedAsRead,
  getAttachmentDownloadUrl,
} from '../../services/Feed.service';
import {
  transformApiConversationToDisplay,
  transformSocketEventToConversation,
  transformSocketEventToMessage,
  generateFeedSettings,
  generateEditorProps,
  checkUserRoleType,
  isNameDocIdLocationsAvailableInSocketData,
  processAttachmentWithState,
  removeTagTypeFromTags,
  createFormDefinitionEditorItem,
  appendFormReferencesToMessageHtml,
  buildAttachedFormMessageAttachments,
  isRelativeUrlMimeType,
  getFeedMessagePreview,
} from '../../mappers/feedUtility';
import {
  PAGINATION_CONSTANTS,
  FEED_TYPE,
  MESSAGE_TYPE,
  TAB_NAMES,
  MEMBERSHIP_TYPE,
  MIME_TYPE_FORM_IO,
} from '../../constants';
import { LOCATIONS, ROLES } from '../../../../../constants';
import { isPermitted } from '../../../../../components/permission-control/permission-control';
import { FEED_CREATE, FEED_ATTACHMENT_CREATE, FEED_UPDATE } from '../../FeedPermission';
import { FORM_ATTACH } from '../../../../Form/constants/form.permission';
import { useFeedMessagesMenuHook } from '../../../../shared/hooks';
import { UnleashService } from '../../../../services';
import useUnleashHook from '../../../../core/unleash/useUnleashHook';
import { useAttachmentUploadHook } from '../attachments/useAttachmentUploadHook';
import {
  getFormDefinitionById,
  submitFormResponse,
} from '../../../../../util/APIUtils';
import { GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS } from '../../../../Form/constants/form-api.constants';

const FORM_SUBMITTED_TOAST_MESSAGE = 'Form has been submitted';
/** Defer closing the attachment modal so Form.io can finish submit/focus without touching a destroyed root. */
const MODAL_CLOSE_AFTER_FORM_MS = 100;

const stripNewTagsFromConversation = (tags) =>
  tags?.filter((tag) => tag.type !== TagEnum.New) || [];

const mapConversationAfterMarkedRead = (conv, conversationId) => {
  if (conv.id !== conversationId) {
    return conv;
  }
  return {
    ...conv,
    isNew: false,
    unreadCount: 0,
    messagePreview: '',
    tags: stripNewTagsFromConversation(conv.tags),
  };
};

const buildMessageAttachmentWithHandlers = ({
  attachment,
  getAttachmentState,
  handleAttachmentClick,
  stableModalCloseByFileId,
}) => {
  const fileId = attachment.fileId || attachment.attachmentId || attachment.fileName;
  const attachmentState = getAttachmentState(fileId);
  const processedAttachment = processAttachmentWithState(attachment, attachmentState);

  const onAttachmentClick = (fileDetails) => {
    handleAttachmentClick({
      ...fileDetails,
      fileId: fileId || fileDetails.fileId,
      attachmentId: fileId || fileDetails.fileId,
      mimeType: fileDetails.mimeType ?? attachment.mimeType,
    });
  };

  const modalCloseKey = fileId != null && fileId !== '' ? String(fileId) : null;
  const onAttachmentModalClose =
    modalCloseKey && stableModalCloseByFileId
      ? stableModalCloseByFileId.get(modalCloseKey)
      : undefined;

  return {
    ...processedAttachment,
    onClick: onAttachmentClick,
    onModalClose: onAttachmentModalClose,
  };
};

/**
 * Hook for managing Direct Messages functionality
 * Gets user data directly from localStorage
 */
export const useDirectMessagesHook = (
  onBackToOriginalTab,
  isActive = false,
  decrementUnreadCount,
  activeTab,
  selectedLocationId = null,
  onAttachFormClick,
) => {
  // State
  // Get user details from Redux store
  const { userDetail } = useSelector((state) => state.app || {});
  const currentUserKey = userDetail?.userId;
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [feedSettings, setFeedSettings] = useState([]);
  const [editorProps, setEditorProps] = useState(null);
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isMessagesLoadingMore, setIsMessagesLoadingMore] = useState(false);
  const [hasLoadedInitialConversations, setHasLoadedInitialConversations] = useState(false);
  const [feedDetails, setFeedDetails] = useState(null);
  const [totalConversationCount, setTotalConversationCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentMessagePage, setCurrentMessagePage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [showNewMessagesIndicator, setShowNewMessagesIndicator] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isFeedDetailLoading, setIsFeedDetailLoading] = useState(true);
  const [global, setGlobal] = useGlobal();
  const [attachmentStates, setAttachmentStates] = useState({}); //attachment states for the current conversation
  const [attachedForms, setAttachedForms] = useState([]);
  const [openFormPreviewFileId, setOpenFormPreviewFileId] = useState(null);
  const [isInactiveFormModalOpen, setIsInactiveFormModalOpen] = useState(false);

  const isMountedRef = useRef(true);
  const loadingConversationRef = useRef(null);
  const selectedConversationRef = useRef(selectedConversation);
  const messageViewRef = useRef(null);
  const searchDebounceTimer = useRef(null);
  const conversationListRef = useRef(conversations);
  const hasSentMessageInSessionRef = useRef(false);
  const previousLocationIdRef = useRef(selectedLocationId);
  const isFirstSearchRender = useRef(true);

  const {
    attachmentFiles,
    snackbarError,
    fileInputRef,
    handleFileChange,
    onFileUploadClick,
    onSnackbarClose,
    showSnackbarMessage,
    resetAttachments,
  } = useAttachmentUploadHook();

  const replaceAttachedForms = useCallback((forms) => {
    if (!Array.isArray(forms)) {
      return;
    }
    setAttachedForms(forms.filter((f) => f != null && f.id != null));
  }, []);

  const removeAttachedForm = useCallback((formId) => {
    setAttachedForms((prev) => prev.filter((f) => f.id !== formId));
  }, []);

  const handleFormPreviewClose = useCallback(() => {
    setOpenFormPreviewFileId(null);
  }, []);

  const onCloseInactiveFormModal = useCallback(() => setIsInactiveFormModalOpen(false), []);

  const onInactiveFormAttachment = useCallback(() => setIsInactiveFormModalOpen(true), []);

  const handleFormPreviewOpen = useCallback(
    async (fileId) => {
      const rawId = String(fileId).replace(/^form-def-/, '');
      try {
        const res = await getFormDefinitionById(rawId, GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS);
        const data = res?.data ?? res;
        const schema = data?.schema;
        if (schema && typeof schema === 'object') {
          setAttachedForms((prev) =>
            prev.map((f) =>
              String(f.id) === rawId ? { ...f, schema } : f,
            ),
          );
        }
        setOpenFormPreviewFileId(fileId);
      } catch (error) {
        if (error?.message === 'INACTIVE_FORM') {
          setIsInactiveFormModalOpen(true);
          return;
        }
        console.error('[DirectMessages] Failed to load form schema:', error);
      }
    },
    [],
  );

  const formPreviewControl = useMemo(
    () => ({
      onOpen: handleFormPreviewOpen,
      onClose: handleFormPreviewClose,
      isOpen: (fid) => openFormPreviewFileId === fid,
    }),
    [handleFormPreviewOpen, handleFormPreviewClose, openFormPreviewFileId],
  );

  const isAttachmentUploadFlagEnabled = useUnleashHook(UnleashService.FLAGS.FEED_ATTACHMENTS_UPLOAD).isFlagEnabled();

  const canUploadAttachment = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FEED_ATTACHMENT_CREATE, permissionIds);
  }, [global.cbacConfigFlag, global.permissions, global.permissionIds]);

  const canAttachForm = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FORM_ATTACH, permissionIds);
  }, [global.cbacConfigFlag, global.permissions, global.permissionIds]);

  const canUpdateConversation = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FEED_UPDATE, permissionIds);
  }, [global.cbacConfigFlag, global.permissions, global.permissionIds]);

  const showNewMessageBtn = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FEED_CREATE, permissionIds);
  }, [global.cbacConfigFlag, global.permissions, global.permissionIds]);

  const canViewLearnerProfile = useMemo(() => {
    // Messaging Only admins and attorneys should not see the learner profile link
    const isMessagingOnly = checkUserRoleType([ROLES.MESSAGING_ONLY]);
    const isAttorney = checkUserRoleType([ROLES.ATTORNEY]);

    if (isMessagingOnly || isAttorney) {
      return false;
    }

    // Only Superadmin, Facility Admin (STAFF), and Facility Staff (TEACHER) can view learner profiles
    return checkUserRoleType([ROLES.SUPER_ADMIN, ROLES.STAFF, ROLES.TEACHER]);
  }, []);

  // Hook for message menu options and modal handlers
  const { menuProps, readReceiptsModal } = useFeedMessagesMenuHook();


  const clearConversationState = useCallback(() => {
    setConversationMessages([]);
    setFeedSettings([]);
    setEditorProps(null);
    resetAttachments();
    setAttachedForms([]);
    setOpenFormPreviewFileId(null);
    setCurrentMessagePage(1);
    setHasMoreMessages(false);
    setShowNewMessagesIndicator(false);
    setIsAtBottom(true);
  }, []);

  const updateConversationsState = useCallback((newConversations, shouldReset) => {
    if (shouldReset) {
      setConversations(newConversations);
    } else {
      setConversations((prev) => [...prev, ...newConversations]);
    }
  }, []);

  const markConversationAsRead = async (conversationId) => {
    try {
      const response = await markFeedAsRead(conversationId);
      const messagesMarkedAsRead = response?.data?.messagesMarkedAsRead || 0;

      setConversations((prev) =>
        prev.map((conv) => mapConversationAfterMarkedRead(conv, conversationId)),
      );

      if (decrementUnreadCount && messagesMarkedAsRead > 0) {
        decrementUnreadCount(FEED_TYPE.DM, messagesMarkedAsRead);
      }
    } catch (error) {
      console.error('[DirectMessages] Failed to mark conversation as read:', error);
      return 0;
    }
  }

  const addMessageToCurrentConversation = useCallback((newMessage) => {
    // Use setConversationMessages directly to ensure menuProps are properly included
    // This matches the pattern used in sendMessage for consistency
    setConversationMessages((prev) => [...prev, newMessage]);
  }, []);

  const updateExistingConversation = useCallback((existingConv, socketData, isCurrentConv) => {
    const tags = isCurrentConv
      ? removeTagTypeFromTags(existingConv.tags, TagEnum.New)
      : [...(existingConv.tags || []), { title: 'New', type: TagEnum.New }].filter(
        (tag, index, arr) => arr.findIndex((t) => t.type === tag.type) === index
      );

    return {
      ...existingConv,
      timestamp: socketData.timestamp,
      messagePreview: isCurrentConv
        ? ''
        : (getFeedMessagePreview(socketData) || existingConv.messagePreview || ''),
      isNew: !isCurrentConv,
      unreadCount: isCurrentConv ? 0 : (existingConv.unreadCount || 0) + 1,
      tags,
    };
  }, []);

  // Send message
  const sendMessage = useCallback(
    async (messageContent) => {
      if (!selectedConversation?.id) {
        console.error('[DirectMessages] No conversation selected for sending message');
        return;
      }
      try {
        const bodyWithForms = appendFormReferencesToMessageHtml(messageContent, attachedForms);
        const formAttachmentsForApi = buildAttachedFormMessageAttachments(attachedForms);
        const attachmentsForSend = [...attachmentFiles, ...formAttachmentsForApi];
        const messageData = {
          body: bodyWithForms || null,
          messageType: MESSAGE_TYPE.TEXT,
          attachments: attachmentsForSend,
        };
        if (attachmentsForSend.length > 0) {
          setGlobal({
            isLoading: true,
          });
        }
        const response = await createMessage(selectedConversation.id, messageData);
        setConversationMessages((prev) => [...prev, transformSocketEventToMessage(response.data, currentUserKey, menuProps)]);
        // Clear alertProps after successful message send
        if (editorProps?.alertProps) {
          setEditorProps(prevProps => ({
            ...prevProps,
            alertProps: undefined
          }));
        }
        hasSentMessageInSessionRef.current = true;
        return response.data;
      } catch (error) {
        console.error('[DirectMessages] Failed to send message:', error);
      } finally {
        // When user sends a message, scroll to bottom and hide indicator
        setGlobal({
          isLoading: false
        });
        resetAttachments();
        setAttachedForms([]);
        setOpenFormPreviewFileId(null);
        setTimeout(() => {
          messageViewRef?.current?.scrollToBottom();
          setIsAtBottom(true);
          setShowNewMessagesIndicator(false);
        }, 100);
      }
    },
    [selectedConversation, editorProps, currentUserKey, menuProps, attachmentFiles, attachedForms, resetAttachments]
  );


  const locationKeySet = useMemo(() => {
    if (selectedLocationId) {
      const locationKeySet = new Set();
      locationKeySet.add(selectedLocationId);
      const childLocations = LOCATIONS.filter((location) => location.parentLocationId === selectedLocationId);
      childLocations.forEach((location) => {
        locationKeySet.add(location.locationId);
      });
      return locationKeySet;
    }
    return new Set();
  }, [selectedLocationId]);

  // Helper function to scroll to bottom and update related state
  const scrollToBottomAndUpdateState = useCallback(() => {
    if (messageViewRef?.current) {
      setTimeout(() => {
        messageViewRef.current?.scrollToBottom();
        setIsAtBottom(true);
        setShowNewMessagesIndicator(false);
      }, 100);
    }
  }, []);

  // Helper function to handle initial page load (page 1)
  const handleInitialPageLoad = useCallback((rawMessages) => {
    setConversationMessages(rawMessages.reverse());
    scrollToBottomAndUpdateState();
  }, [scrollToBottomAndUpdateState]);

  // Helper function to handle pagination (page > 1)
  const handlePaginationLoad = useCallback((rawMessages) => {
    setConversationMessages((prev) => {
      const reversedNewMessages = rawMessages.reverse();
      return [...reversedNewMessages, ...prev];
    });
    setIsAtBottom(false);
  }, []);

  // Helper function to update pagination state
  const updatePaginationState = useCallback((page, hasMore) => {
    setCurrentMessagePage(page);
    setHasMoreMessages(hasMore || false);
  }, []);

  const loadMessagesAsync = useCallback(
    async (conversationId, page = 1) => {
      const messagesResponse = await getFeedMessages(conversationId, {
        page,
        size: PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
        currentUserKey,
        menuProps,
      });

      if (!isMountedRef.current) {
        return messagesResponse;
      }

      const rawMessages = messagesResponse.data || [];

      if (page === 1) {
        handleInitialPageLoad(rawMessages);
      } else {
        handlePaginationLoad(rawMessages);
      }

      updatePaginationState(page, messagesResponse.hasMore);

      return messagesResponse;
    },
    [currentUserKey, menuProps, handleInitialPageLoad, handlePaginationLoad, updatePaginationState]
  );

  const loadFeedDetailsAsync = useCallback(async (conversationId) => {
    setIsFeedDetailLoading(true);
    const feedDetailsResponse = await getFeedById(conversationId);

    setIsFeedDetailLoading(false);
    if (isMountedRef.current) {
      const feedDetails = feedDetailsResponse.data || null;
      return feedDetails;
    }
    return feedDetailsResponse.data;
  }, []);

  const refreshFeedDetails = useCallback(
    async (conversationId) => {
      if (!conversationId) return;

      try {
        const feedDetails = await loadFeedDetailsAsync(conversationId);
        if (feedDetails) {
          handleFeedDetailsLoaded(feedDetails);
        }
      } catch (error) {
        console.error('[DirectMessages] Failed to refresh feed details:', error);
      }
    },
    [loadFeedDetailsAsync]
  );

  // Update selectedConversation with membersInfo and type from feedDetails
  const updateSelectedConversationWithFeedDetails = useCallback((feedDetails) => {
    if (!feedDetails.membersInfo || !selectedConversationRef.current) {
      return;
    }

    setSelectedConversation((prev) => {
      if (prev && prev.id === feedDetails.id) {
        const feedType = feedDetails.feedType === 'DM' ? FeedTypeEnum.dm : prev.type;
        return {
          ...prev,
          type: prev.type || feedType,
          membersInfo: feedDetails.membersInfo,
        };
      }
      return prev;
    });
  }, []);

  const handleFeedDetailsLoaded = useCallback(
    (feedDetails) => {
      if (!isMountedRef.current || !feedDetails) return

      const generatedSettings = generateFeedSettings(feedDetails);
      const generatedEditorProps = generateEditorProps(feedDetails, sendMessage);
      setFeedSettings(generatedSettings);
      setEditorProps(generatedEditorProps);
      setFeedDetails(feedDetails);

      updateSelectedConversationWithFeedDetails(feedDetails);
    },
    [sendMessage, updateSelectedConversationWithFeedDetails]
  );

  const handleFeedDetailsError = useCallback((error) => {
    console.error('[DirectMessages] Failed to load feed details:', error);
  }, []);

  const openConversation = useCallback(
    async (conversationData) => {
      const conversationId = conversationData.id;
      if (!conversationId) {
        console.error('[DirectMessages] Invalid conversation data:', conversationData);
        return;
      }

      if (loadingConversationRef.current === conversationId) {
        return;
      }

      loadingConversationRef.current = conversationId;

      setSelectedConversation(conversationData);
      setIsMessagesLoading(true);
      clearConversationState();
      setCurrentMessagePage(1);
      setHasMoreMessages(false);

      // Mark conversation as read when opened
      await markConversationAsRead(conversationId);
      // Only reload if there's no active search to preserve search results
      if (!searchQuery) {
        loadConversations(1, true);
      }


      const messagesPromise = loadMessagesAsync(conversationId).finally(() => {
        if (loadingConversationRef.current === conversationId) {
          setIsMessagesLoading(false);
        }
      });
      const detailsPromise = loadFeedDetailsAsync(conversationId)
        .then(handleFeedDetailsLoaded)
        .catch(handleFeedDetailsError);

      Promise.allSettled([messagesPromise, detailsPromise]).finally(() => {
        if (isMountedRef.current) {
          loadingConversationRef.current = null;
        }
      });
    },
    [
      loadMessagesAsync,
      loadFeedDetailsAsync,
      clearConversationState,
      handleFeedDetailsLoaded,
      handleFeedDetailsError,
      decrementUnreadCount
    ]
  );

  const transformConversationsForDisplay = useCallback(
    (conversations) => {
      return conversations.map((conversationData) => {
        const displayConversation = transformApiConversationToDisplay(conversationData, currentUserKey, 'dm');
        return {
          ...displayConversation,
          onFeedSelectHandler: openConversation,
          onViewFullHandler: openConversation,
        };
      });
    },
    [currentUserKey, openConversation]
  );

  const loadConversations = useCallback(
    async (page = 1, shouldReset = false) => {
      if (!isMountedRef.current) return;

      if (shouldReset) {
        setCurrentPage(1);
      }

      setIsConversationsLoading(true);
      try {
        const response = await getDMFeeds({
          page,
          size: PAGINATION_CONSTANTS.DEFAULT_DM_PAGE_SIZE,
          currentUserKey,
          searchTerm: searchQuery,
          locationKeys: selectedLocationId ? [selectedLocationId] : []
        });
        const { content = [], totalElements = 0, hasMore: responseHasMore = false } = response.data || {};

        if (content.length > 0) {
          const transformedConversations = transformConversationsForDisplay(content);
          updateConversationsState(transformedConversations, shouldReset);
        } else if (shouldReset) {
          setConversations([]);
        }

        setTotalConversationCount(totalElements);
        setHasLoadedInitialConversations(true);
        setHasMore(responseHasMore);
      } catch (error) {
        setHasLoadedInitialConversations(true); // Add this line such that infinite loop does not trigger for fetching again
        console.error('[DirectMessages] Failed to load conversations:', error);
      } finally {
        if (isMountedRef.current) {
          setIsConversationsLoading(false);
        }
      }
    },
    [searchQuery, currentUserKey, transformConversationsForDisplay, updateConversationsState, selectedLocationId, setCurrentPage]
  );

  const loadMoreMessagesAsync = useCallback(
    async (conversationId) => {
      // Guard conditions: prevent multiple simultaneous loads
      if (
        isMessagesLoadingMore ||
        isMessagesLoading ||
        conversationMessages.length === 0 ||
        !hasMoreMessages
      ) {
        return;
      }

      setIsMessagesLoadingMore(true);
      try {
        const nextPage = currentMessagePage + 1;
        await loadMessagesAsync(conversationId, nextPage);
      } catch (error) {
        console.error('[DirectMessages] Failed to load more messages:', error);
      } finally {
        if (isMountedRef.current) {
          setIsMessagesLoadingMore(false);
        }
      }
    },
    [
      isMessagesLoadingMore,
      isMessagesLoading,
      conversationMessages.length,
      hasMoreMessages,
      currentMessagePage,
      loadMessagesAsync,
    ]
  );

  const closeConversation = useCallback(() => {
    loadingConversationRef.current = null;
    setIsMessagesLoading(false);
    setSelectedConversation(null);
    setConversationMessages([]);
    setFeedSettings([]);
    setEditorProps(null);
    resetAttachments();
    setAttachedForms([]);
    setOpenFormPreviewFileId(null);
    setCurrentMessagePage(1);
    setHasMoreMessages(false);
    setShowNewMessagesIndicator(false);
    setIsAtBottom(true);
    if (onBackToOriginalTab) {
      onBackToOriginalTab();
    }
    // Reload conversations if:
    // 1. User sent messages AND there's no active search (preserve search results), OR
    // 2. A location filter is active (to ensure conversations are filtered correctly)
    if ((hasSentMessageInSessionRef.current && !searchQuery) || selectedLocationId) {
      loadConversations(1, true);
      hasSentMessageInSessionRef.current = false;
    }
  }, [onBackToOriginalTab, searchQuery, loadConversations, selectedLocationId, resetAttachments]);

  // Socket event handlers
  const handleNewConversationCreated = useCallback(
    (socketData) => {
      const feedType = socketData?.feed_type || socketData?.metadata?.feed_type;
      if (feedType?.toUpperCase() !== FEED_TYPE.DM) return;
      if (!isNameDocIdLocationsAvailableInSocketData(socketData, searchQuery, locationKeySet)) return;

      const newConversation = transformSocketEventToConversation(socketData, currentUserKey, false);
      const conversationWithHandlers = {
        ...newConversation,
        onFeedSelectHandler: openConversation,
        onViewFullHandler: openConversation,
      };

      setConversations((prev) => [conversationWithHandlers, ...prev]);
      setTotalConversationCount((prev) => prev + 1);
    },
    [searchQuery, currentUserKey, openConversation]
  );

  const handleNewMessageInConversation = useCallback(
    (socketData) => {
      if (!isNameDocIdLocationsAvailableInSocketData(socketData, searchQuery, locationKeySet)) return;

      const feedType = socketData?.feed_type || socketData?.metadata?.feed_type;
      if (feedType?.toUpperCase() !== FEED_TYPE.DM) return;

      const conversationId = String(socketData.feed_id || '');
      const isCurrentConversation = selectedConversationRef.current?.id != null && String(selectedConversationRef.current.id) === conversationId;

      if (isCurrentConversation) {
        const newMessage = transformSocketEventToMessage(socketData, currentUserKey, menuProps);
        // Check if the message is from the current user (sent by them)
        const isFromCurrentUser = newMessage.userId === currentUserKey;

        addMessageToCurrentConversation(newMessage);
        markConversationAsRead(conversationId);

        // Only auto-scroll if user is at bottom or if it's their own message
        // If user is scrolled up and message is from someone else, show indicator
        // The component will handle scroll detection and call onNewMessagesIndicatorDismiss when user scrolls to bottom
        if (isFromCurrentUser) {
          // User's own message - always scroll to bottom (if not loading more)
          if (!isMessagesLoadingMore) {
            messageViewRef?.current?.scrollToBottom();
            setIsAtBottom(true);
            setShowNewMessagesIndicator(false);
          }
        } else if (isAtBottom && !isMessagesLoadingMore) {
          // User is at bottom and not loading more - scroll to show new message
          messageViewRef?.current?.scrollToBottom();
          setIsAtBottom(true);
          setShowNewMessagesIndicator(false);
        } else {
          // User is scrolled up or loading more messages - show indicator
          setIsAtBottom(false);
          setShowNewMessagesIndicator(true);
        }
      }
      else {
        setConversations((prev) => {
          const existingConversation = prev.find((conv) => conv.id === conversationId);

          if (existingConversation) {
            const updatedConversation = updateExistingConversation(existingConversation, socketData, isCurrentConversation);
            return [updatedConversation, ...prev.filter((conv) => conv.id !== conversationId)];
          } else {
            const newConversation = transformSocketEventToConversation(socketData, currentUserKey, isCurrentConversation);
            newConversation.onFeedSelectHandler = openConversation;
            return [newConversation, ...prev];
          }
        });

      }
    },
    [searchQuery, currentUserKey, isAtBottom, isMessagesLoadingMore, addMessageToCurrentConversation, updateExistingConversation, menuProps, openConversation]
  );

  // UI event handlers
  const handleSearchChange = useCallback((event) => {
    setIsConversationsLoading(true);
    setTotalConversationCount(0);
    setConversations([]);
    setCurrentPage(1);
    setSearchQuery(event.target.value);
  }, [setSearchQuery, setCurrentPage, setIsConversationsLoading, setTotalConversationCount, setConversations]);

  const loadMoreConversations = useCallback(() => {
    if (hasMore && !isConversationsLoading) {
      setCurrentPage((currentPage) => {
        const newPage = currentPage + 1
        if (isActive && hasLoadedInitialConversations && !selectedConversation) {
          loadConversations(newPage, false);
        }
        return newPage
      });
    }
  }, [hasMore, isActive, hasLoadedInitialConversations, isConversationsLoading, selectedConversation]);


  // Memoize the new message handler to prevent unnecessary re-renders
  const handleNewMessage = useCallback(() => {
  }, []);

  // Handle when indicator should be dismissed
  // Called by the component when user scrolls to bottom or clicks the indicator
  const handleIndicatorDismiss = useCallback(() => {
    setShowNewMessagesIndicator(false);
    setIsAtBottom(true);
  }, []);

  // Effects

  useEffect(() => {
    // Close conversation when switching away from Messages tab
    // Only close if activeTab is explicitly set and not MESSAGES
    if (activeTab !== undefined && activeTab !== TAB_NAMES.MESSAGES && selectedConversation) {
      closeConversation();
    }
  }, [activeTab, selectedConversation, closeConversation]);

  useEffect(() => {
    // Skip debounced search on initial mount with empty search query
    // This prevents an extra API call on initial page load
    if (isFirstSearchRender.current && searchQuery === '') {
      isFirstSearchRender.current = false;
      return;
    }
    isFirstSearchRender.current = false;

    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      loadConversations(1, true);
    }, 1000);
  }, [
    searchQuery
  ]);

  useEffect(() => {
    conversationListRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);


  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      loadingConversationRef.current = null;
    };
  }, []);

  // Load initial conversations when tab becomes active
  useEffect(() => {
    // Only load conversations if:
    // 1. Tab is active
    // 2. We haven't loaded initial conversations yet
    // 3. We're not currently loading
    // 4. We don't have any conversations already (to prevent reloading when opening conversations)
    // 5. No conversation is currently selected (to prevent reloading when opening conversations)
    if (isActive && !hasLoadedInitialConversations && !isConversationsLoading && conversations.length === 0 && !selectedConversation) {
      loadConversations(1, true);
    }
  }, [isActive, hasLoadedInitialConversations, isConversationsLoading, conversations.length, selectedConversation, loadConversations]);

  // Handle location filter changes
  const handleLocationFilterChange = () => {
    const previousLocationId = previousLocationIdRef.current;
    const currentLocationId = selectedLocationId;

    // Skip on initial mount when both previous and current are null/undefined
    // This prevents an extra API call on initial page load
    const isInitialMount = (previousLocationId === null || previousLocationId === undefined) &&
      (currentLocationId === null || currentLocationId === undefined);

    // Check if location actually changed
    const locationChanged = previousLocationId !== currentLocationId;

    // Only reload if location changed, not initial mount, and conditions are met
    const shouldReload = !isInitialMount &&
      locationChanged &&
      isActive &&
      hasLoadedInitialConversations &&
      !selectedConversation &&
      !isConversationsLoading;

    if (shouldReload) {
      previousLocationIdRef.current = currentLocationId;
      loadConversations(1, true);
    } else if (locationChanged) {
      // Update ref even if we don't reload (e.g., when tab is not active or initial mount)
      previousLocationIdRef.current = currentLocationId;
    }
  };

  // Reload conversations when location filter changes
  useEffect(() => {
    handleLocationFilterChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId]);


  // Memoized values

  const showLoadMore = useMemo(() => {
    return hasMore && !isConversationsLoading;
  }, [hasMore, isConversationsLoading]);

  const userProps = useMemo(() => {
    return {
      userId: currentUserKey,
      firstName: userDetail.firstName,
      lastName: userDetail.lastName,
    };
  }, [currentUserKey, userDetail.firstName, userDetail.lastName]);

  // Extract learner ID from membersInfo for DM feeds
  const learnerId = useMemo(() => {
    if (
      selectedConversation &&
      selectedConversation.type === FeedTypeEnum.dm &&
      selectedConversation.membersInfo &&
      Array.isArray(selectedConversation.membersInfo) &&
      selectedConversation.membersInfo.length > 0 &&
      currentUserKey
    ) {
      // Find the member that's not the current user
      const learnerMember = selectedConversation.membersInfo.find(
        (member) =>
          member.type === MEMBERSHIP_TYPE.PERSON &&
          String(member.id) !== String(currentUserKey)
      );
      return learnerMember?.id || null;
    }
    return null;
  }, [selectedConversation, currentUserKey]);

  // Navigation handler for learner profile
  const navigate = useNavigate();
  const handleNavigateToLearnerProfile = useCallback((learnerId) => {
    navigate(`/users/${learnerId}`);
  }, [navigate]);

  const formAttachmentFetchSchema = useCallback(async (formId) => {
    const response = await getFormDefinitionById(formId);
    const data = response?.data ?? response;
    const schema = data?.schema;
    if (schema && typeof schema === 'object') {
      return schema;
    }
    throw new Error('Invalid form definition response');
  }, []);

  // Callback to fetch attachment download URL
  const handleFetchAttachmentUrl = useCallback(async (attachmentId) => {
    try {
      const response = await getAttachmentDownloadUrl(attachmentId);
      // Response structure: { status: 200, data: { presignedUrl: '...', ... } }
      if (response?.data?.presignedUrl) {
        return response.data.presignedUrl;
      }
      throw new Error('Presigned URL not found in response');
    } catch (error) {
      console.error('[DirectMessages] Failed to fetch attachment download URL:', error);
      throw error;
    }
  }, []);

  // Helper: Extract and validate fileId from fileDetails
  const getFileId = useCallback((fileDetails) => {
    const fileId = fileDetails.fileId || fileDetails.attachmentId;
    if (!fileId) {
      console.warn('[DirectMessages] Cannot handle attachment click: missing fileId');
    }
    return fileId;
  }, []);

  // Helper: Open modal with cached URL
  const openModalWithCachedUrl = useCallback((fileId) => {
    setAttachmentStates((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        isModalOpen: true,
      },
    }));
  }, []);

  // Helper: Open modal with provided URL (backward compatibility)
  const openModalWithProvidedUrl = useCallback((fileId, fileUrl) => {
    setAttachmentStates((prev) => ({
      ...prev,
      [fileId]: {
        fileUrl,
        loading: false,
        isModalOpen: true,
      },
    }));
  }, []);

  // Helper: Set error state for attachment
  const setAttachmentError = useCallback((fileId, errorMessage) => {
    setAttachmentStates((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        loading: false,
        isModalOpen: false,
        error: errorMessage,
      },
    }));
  }, []);

  // Helper: Set loading state for attachment
  const setAttachmentLoading = useCallback((fileId) => {
    setAttachmentStates((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        loading: true,
        error: undefined,
      },
    }));
  }, []);

  // Helper: Cache URL and open modal after successful fetch
  const cacheUrlAndOpenModal = useCallback((fileId, presignedUrl) => {
    setAttachmentStates((prev) => ({
      ...prev,
      [fileId]: {
        fileUrl: presignedUrl,
        loading: false,
        isModalOpen: true,
        error: undefined,
      },
    }));
  }, []);

  // Helper: Fetch and cache attachment URL
  const fetchAndCacheAttachmentUrl = useCallback(async (fileId) => {
    if (!handleFetchAttachmentUrl) {
      console.warn('[DirectMessages] Cannot fetch attachment URL: handleFetchAttachmentUrl not provided');
      setAttachmentError(fileId, 'URL fetch not available');
      return;
    }

    setAttachmentLoading(fileId);

    try {
      const presignedUrl = await handleFetchAttachmentUrl(fileId);
      cacheUrlAndOpenModal(fileId, presignedUrl);
    } catch (error) {
      console.error('[DirectMessages] Failed to fetch attachment URL:', error);
      setAttachmentError(fileId, 'Failed to load attachment');
    }
  }, [handleFetchAttachmentUrl, setAttachmentError, setAttachmentLoading, cacheUrlAndOpenModal]);

  // Handle attachment click - fetch URL if not cached, then open modal
  const handleAttachmentClick = useCallback(async (fileDetails) => {
    if (isRelativeUrlMimeType(fileDetails.mimeType)) {
      return;
    }

    const fileId = getFileId(fileDetails);
    if (!fileId) {
      return;
    }

    if (fileDetails.mimeType === MIME_TYPE_FORM_IO) {
      openModalWithCachedUrl(fileId);
      return;
    }

    // Check if URL is already cached
    const currentState = attachmentStates[fileId];
    if (currentState?.fileUrl) {
      openModalWithCachedUrl(fileId);
      return;
    }

    // If fileUrl is already provided in fileDetails, use it (backward compatibility)
    if (fileDetails.fileUrl) {
      openModalWithProvidedUrl(fileId, fileDetails.fileUrl);
      return;
    }

    // Need to fetch URL
    await fetchAndCacheAttachmentUrl(fileId);
  }, [attachmentStates, getFileId, openModalWithCachedUrl, openModalWithProvidedUrl, fetchAndCacheAttachmentUrl]);

  // Handle attachment modal close
  const handleAttachmentModalClose = useCallback((fileId) => {
    setAttachmentStates((prev) => {
      const currentState = prev[fileId];
      if (!currentState) return prev;
      return {
        ...prev,
        [fileId]: {
          ...currentState,
          isModalOpen: false,
        },
      };
    });
  }, []);

  const onFormAttachmentSubmit = useCallback(
    async ({ formId, submission, messageId, fileId }) => {
      const defRes = await getFormDefinitionById(formId, GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS);
      const def = defRes?.data ?? defRes;
      if (def && def.active === false) {
        throw new Error('INACTIVE_FORM');
      }
      const payloadData =
        submission &&
        typeof submission === 'object' &&
        submission !== null &&
        'data' in submission &&
        submission.data !== undefined
          ? submission.data
          : submission;
      await submitFormResponse(formId, {
        data: payloadData,
        reference_id:
          messageId != null && messageId !== ''
            ? String(messageId)
            : undefined,
      });
      showSnackbarMessage(FORM_SUBMITTED_TOAST_MESSAGE, { severity: 'success' });
      if (fileId != null && fileId !== '') {
        setTimeout(() => {
          handleAttachmentModalClose(fileId);
        }, MODAL_CLOSE_AFTER_FORM_MS);
      }
    },
    [handleAttachmentModalClose, showSnackbarMessage],
  );

  /**
   * Stable `onModalClose` per attachment file id (rebuilt only when messages change).
   * Avoids repeated Form.io definition fetches when `attachmentStates` updates (see `AttachmentChip` form load effect).
   */
  const stableModalCloseByFileId = useMemo(() => {
    const map = new Map();
    const addClose = (rawId) => {
      if (rawId == null || rawId === '') {
        return;
      }
      const key = String(rawId);
      if (map.has(key)) {
        return;
      }
      map.set(key, () => {
        handleAttachmentModalClose(rawId);
      });
    };
    for (const message of conversationMessages) {
      for (const att of message.attachments || []) {
        addClose(att.fileId ?? att.attachmentId ?? att.fileName);
      }
    }
    return map;
  }, [conversationMessages, handleAttachmentModalClose]);

  // Get attachment state for a specific attachment
  const getAttachmentState = useCallback((fileId) => {
    if (!fileId) return undefined;
    return attachmentStates[fileId] || {
      loading: false,
      isModalOpen: false,
    };
  }, [attachmentStates]);

  /** Attachment chip props + click/modal handlers; useCallback keeps messageView useMemo shallow (sonarjs/no-nested-functions). */
  const buildMessageAttachmentViewProps = useCallback(
    (processedAttachment, fileId) => ({
      ...processedAttachment,
      onClick: (fileDetails) => {
        handleAttachmentClick({
          ...fileDetails,
          fileId: fileId || fileDetails.fileId,
          attachmentId: fileId || fileDetails.fileId,
        });
      },
      onModalClose: fileId ? () => handleAttachmentModalClose(fileId) : undefined,
    }),
    [handleAttachmentClick, handleAttachmentModalClose]
  );

  const messageView = useMemo(() => {
    if (!selectedConversation) {
      return undefined;
    }

    // Process messages with fully computed attachments
    const processedMessages = conversationMessages.map((message) => {
      if (!message.attachments || message.attachments.length === 0) {
        return message;
      }

      return {
        ...message,
        attachments: message.attachments.map((attachment) =>
          buildMessageAttachmentWithHandlers({
            attachment,
            getAttachmentState,
            handleAttachmentClick,
            stableModalCloseByFileId,
          }),
        ),
      };
    });

    const formEditorItems = attachedForms
      .map((form) =>
        createFormDefinitionEditorItem(form, removeAttachedForm, formPreviewControl),
      )
      .filter(Boolean);
    const filesListForEditor = [...attachmentFiles, ...formEditorItems];

    return {
      isLoading: isMessagesLoading,
      messages: processedMessages,
      selectedFeed: {
        ...selectedConversation,
        settings: feedSettings,
      },
      showLoadMore: showLoadMore,
      userProps,
      onBackHandler: closeConversation,
      onMessageSendHandler: (feed, msgHTML) => sendMessage(msgHTML),
      onSendHandler: sendMessage,
      editorProps: {
        ...editorProps,
        filesList: filesListForEditor,
        onFileUploadClick,
        onAttachFormClick,
        snackbarError: snackbarError,
        onSnackbarClose,
        toolbarOptions: {
          ...(editorProps?.toolbarOptions || {}),
          fileUpload: (isAttachmentUploadFlagEnabled && canUploadAttachment) || false,
          attachForm: canAttachForm && Boolean(onAttachFormClick),
        },
      },
      showSettingsIcon: canUpdateConversation && !isFeedDetailLoading && !feedDetails?.isLocked,
      onLoadMoreHandler: () => loadMoreMessagesAsync(selectedConversation.id),
      showNewMessagesIndicator: showNewMessagesIndicator,
      onNewMessagesIndicatorDismiss: handleIndicatorDismiss,
      hasMoreMessages: hasMoreMessages,
      canViewLearnerProfile,
      learnerId,
      onNavigateToLearnerProfile: handleNavigateToLearnerProfile,
      fileInputRef,
      handleFileChange,
      formAttachmentFetchSchema,
      onFormAttachmentSubmit,
      onInactiveFormAttachment,
    };
  }, [
    selectedConversation,
    isMessagesLoading,
    conversationMessages,
    attachmentStates,
    getAttachmentState,
    userProps,
    editorProps,
    feedSettings,
    attachmentFiles,
    attachedForms,
    removeAttachedForm,
    formPreviewControl,
    closeConversation,
    sendMessage,
    hasMoreMessages,
    loadMoreMessagesAsync,
    showNewMessagesIndicator,
    handleIndicatorDismiss,
    canViewLearnerProfile,
    learnerId,
    handleNavigateToLearnerProfile,
    buildMessageAttachmentViewProps,
    getAttachmentState,
    handleAttachmentClick,
    stableModalCloseByFileId,
    handleFileChange,
    feedDetails,
    isAttachmentUploadFlagEnabled,
    canUploadAttachment,
    canAttachForm,
    canUpdateConversation,
    onAttachFormClick,
    formAttachmentFetchSchema,
    onFormAttachmentSubmit,
    onInactiveFormAttachment,
  ]);

  return {
    currentPage,
    userDetail,
    searchQuery,
    feedDetails,
    conversations,
    isLoading: isConversationsLoading,
    totalConversationCount,
    messageView,
    messageViewRef,
    showLoadMore,
    feedSettings,
    showNewMessageBtn,
    isFeedDetailLoading,
    loadConversations,
    openConversation,
    sendMessage,
    onSearchHandler: handleSearchChange,
    onLoadMore: loadMoreConversations,
    onNewMessageHandler: handleNewMessage,
    setConversations,
    handleFeedDetailsLoaded,
    attachmentFiles,
    // Expose refs for useFeedsManager
    selectedConversationRef,
    refreshFeedDetails,
    // Socket event handlers (called from useFeedsManager)
    onConversationCreated: handleNewConversationCreated,
    onMessageInConversation: handleNewMessageInConversation,
    // Modal state
    readReceiptsModal,
    menuProps,
    fileInputRef,
    handleFileChange,
    attachedForms,
    replaceAttachedForms,
    isInactiveFormModalOpen,
    onCloseInactiveFormModal,

    handleAttachmentClick,
  };
};
